import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Reveal, CreditChip } from "@/components/site/primitives";

/**
 * ProgramHero — shared full-bleed hero for the Programs landing and the two
 * individual program pages.
 * @param {string} eyebrow  Small uppercase kicker.
 * @param {string} title    Page headline.
 * @param {string} lede     Supporting paragraph.
 * @param {string} image    Background image (local worldfarmers asset).
 * @param {string} imageAlt Alt text.
 * @param {{label:string, href:string}} [back]  Optional back link (sub-pages).
 */
export const ProgramHero = ({
  eyebrow,
  title,
  lede,
  image,
  imageAlt,
  back,
}) => (
  <section id="top" className="relative flex min-h-[68vh] items-end overflow-hidden bg-[var(--wf-soil)]">
    <img src={image} alt={imageAlt} loading="eager" className="absolute inset-0 h-full w-full object-cover" />
    <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/25" />
    <div aria-hidden="true" className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/40 to-transparent" />

    <div className="wf-container relative z-10 pb-16 pt-36 sm:pb-24">
      <Reveal className="max-w-3xl">
        {back && (
          <Link
            to={back.href}
            data-testid="program-hero-back-link"
            className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/10 px-4 py-2 text-xs font-semibold text-white backdrop-blur-sm transition-colors duration-200 hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
            {back.label}
          </Link>
        )}
        <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.24em] text-[rgba(255,253,248,0.85)]">
          <span aria-hidden="true" className="h-px w-8 bg-[rgba(255,253,248,0.6)]" />
          {eyebrow}
        </span>
        <h1
          data-testid="program-hero-headline"
          className="mt-5 font-serif text-4xl font-semibold leading-[1.05] tracking-[-0.01em] text-white [text-shadow:0_2px_24px_rgba(0,0,0,0.45)] sm:text-6xl lg:text-7xl"
        >
          {title}
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-7 text-[rgba(255,253,248,0.92)] sm:text-lg">{lede}</p>
      </Reveal>
    </div>

    <CreditChip src={image} className="absolute bottom-3 right-3 z-10 rounded-full bg-black/45 px-3 py-1 text-[11px] font-medium text-white/90 backdrop-blur-sm hover:bg-black/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white" />
  </section>
);
